import { createAction, createReducer } from '@reduxjs/toolkit';
import { addQueryParams, localTime, machineStatus, navigationRef, removeQueryParam } from '@helpers';
import { backendCall, createApiCall } from '@reducers/utils';
import type { AppDispatch } from '@reducers/utils/types';
import type { RootState } from '@store/types';
import type { MachinesV2State, StatusesCount, Machine, RetrieveRawMachineEventDataRequestDto, MachineGroup } from './types';

// Re-export types for consumers
export type { MachinesV2State, StatusesCount, Machine, MachineGroup, RetrieveRawMachineEventDataResponse } from './types';

const initState: MachinesV2State = {
  isMachinesSelectModalOpen: false,
  modalMissingPIOpen: false,
  actionsModalOpen: false,
  chartData: {},
  selectedTimeCounter: 0,
  selectedChartDate: null,
  selectedChartDateEnd: null,
  initialEventDate: null,
  initialLoading: true,
  allowLoadMoreData: false,
  loadDataDisabled: false,
  displayScrollViewLoader: false,
  selectedGroup: null,
  isRemovingPI: false,
  statusesCount: {
    [machineStatus.ACTIVE]: 0,
    [machineStatus.SETUP]: 0,
    [machineStatus.CHANGE]: 0,
    [machineStatus.NOTPRODUCTIVE]: 0,
  },
  firstRenderMachinesProvider: true,
  shouldShowGlobalFullNCFileButton: true,
  selectedMachineRunningTime: 0,
};

// selectors
export const selectSelectedChartDate = (state: RootState) => state.machinesV2.selectedChartDate;
export const selectSelectedChartDateEnd = (state: RootState) => state.machinesV2.selectedChartDateEnd;
export const selectSelectedTimeCounter = (state: RootState) => state.machinesV2.selectedTimeCounter;
export const selectSelectedGroup = (state: RootState) => state.machinesV2.selectedGroup;
export const selectShouldShowGlobalFullNCFileButton = (state: RootState) => state.machinesV2.shouldShowGlobalFullNCFileButton;
export const selectSelectedMachineRunningTime = (state: RootState) => state.machinesV2.selectedMachineRunningTime;
export const selectStatusesCount = (state: RootState) => state.machinesV2.statusesCount;

//actions
export const setIsMachinesSelectModalOpenAction = createAction<boolean>('setIsMachinesSelectModalOpenAction');
export const setModalMissingPIOpenAction = createAction<boolean>('setModalMissingPIOpenAction');
export const setActionsModalOpenAction = createAction<boolean>('setActionsModalOpenAction');
export const setChartDataAction = createAction<Record<string, unknown>>('setChartDataAction');
export const setSelectedTimeCounterAction = createAction('setSelectedTimeCounterAction');
export const setSelectedChartDateAction = createAction<string>('setSelectedChartDateAction');
export const setSelectedChartDateEndAction = createAction<string>('setSelectedChartDateEndAction');
export const setInitialLoadingAction = createAction<boolean>('setInitialLoadingAction');
export const setInitialEventDateAction = createAction<string | null>('setInitialEventDateAction');
export const setAllowLoadMoreDataAction = createAction<boolean>('setAllowLoadMoreDataAction');
export const setLoadDataDisabledAction = createAction<boolean>('setLoadDataDisabledAction');
export const setDisplayScrollViewLoaderAction = createAction<boolean>('setDisplayScrollViewLoaderAction');

export const setSelectedGroupAction = createAction<MachineGroup | null>('setSelectedGroupAction');
export const setStatusesCountAction = createAction<StatusesCount>('setStatusesCountAction');
export const setIsRemovingPIAction = createAction<boolean>('setIsRemovingPIAction');
export const setFirstRenderMachinesProviderAction = createAction<boolean>('setFirstRenderMachinesProviderAction');
export const setShouldShowGlobalFullNCFileButtonAction = createAction<boolean>('setShouldShowGlobalFullNCFileButtonAction');
export const setSelectedMachineRunningTimeAction = createAction<number>('setSelectedMachineRunningTimeAction');

// methods
export const setIsMachinesSelectModalOpen = (state: boolean) => (dispatch: AppDispatch) => {
  dispatch(setIsMachinesSelectModalOpenAction(state));
};

export const setModalMissingPIOpen = (state: boolean) => (dispatch: AppDispatch) => {
  dispatch(setModalMissingPIOpenAction(state));
};

export const setActionsModalOpen = (state: boolean) => (dispatch: AppDispatch) => {
  dispatch(setActionsModalOpenAction(state));
};

export const setChartData = (state: Record<string, unknown>) => (dispatch: AppDispatch) => {
  dispatch(setChartDataAction(state));
};

export const setSelectedTimeCounter = () => (dispatch: AppDispatch) => {
  dispatch(setSelectedTimeCounterAction());
};

export const setSelectedChartDate = (date: string | null) => (dispatch: AppDispatch) => {
  if (date) {
    const formattedDate = localTime(date).format();
    navigationRef.current?.setParams({ date: formattedDate });
    dispatch(setSelectedChartDateAction(formattedDate));
  } else {
    navigationRef.current?.setParams({ date: undefined });
  }
};

export const setSelectedChartDateEnd = (date: string | null) => (dispatch: AppDispatch) => {
  if (date) {
    const formattedDate = localTime(date).format();
    navigationRef.current?.setParams({ dateEnd: formattedDate });
    dispatch(setSelectedChartDateEndAction(formattedDate));
  } else {
    navigationRef.current?.setParams({ dateEnd: undefined });
  }
};

export const setInitialLoading = (state: boolean) => (dispatch: AppDispatch) => {
  dispatch(setInitialLoadingAction(state));
};

export const setInitialEventDate = (date: string | null) => (dispatch: AppDispatch) => {
  if (date) {
    addQueryParams({ eventsDate: localTime(date).format('YYYY-MM-DD') });
  } else {
    removeQueryParam('eventsDate');
  }
  dispatch(setInitialEventDateAction(date));
};

export const setAllowLoadMoreData = (state: boolean) => (dispatch: AppDispatch) => {
  dispatch(setAllowLoadMoreDataAction(state));
};

export const setLoadDataDisabled = (state: boolean) => (dispatch: AppDispatch) => {
  dispatch(setLoadDataDisabledAction(state));
};

export const setDisplayScrollViewLoader = (state: boolean) => (dispatch: AppDispatch) => {
  dispatch(setDisplayScrollViewLoaderAction(state));
};

export const setSelectedGroup = (state: MachineGroup | null) => (dispatch: AppDispatch) => {
  dispatch(setSelectedGroupAction(state));
};

export const setStatusesCount = (data: Machine[]) => (dispatch: AppDispatch) => {
  let count: StatusesCount = { ...initState.statusesCount };
  data.forEach((item) => {
    const status = item?.machineStatus?.status;
    if (status) {
      count[status] += 1;
    }
  });
  dispatch(setStatusesCountAction(count));
};

export const setIsRemovingPI = (state: boolean) => (dispatch: AppDispatch) => {
  dispatch(setIsRemovingPIAction(state));
};

export const setFirstRenderMachinesProvider = (state: boolean) => (dispatch: AppDispatch) => {
  dispatch(setFirstRenderMachinesProviderAction(state));
};

export const setShouldShowGlobalFullNCFileButton = (state: boolean) => (dispatch: AppDispatch) => {
  dispatch(setShouldShowGlobalFullNCFileButtonAction(state));
};

export const setSelectedMachineRunningTime = (time: number) => (dispatch: AppDispatch) => {
  dispatch(setSelectedMachineRunningTimeAction(time));
};

export const retrieveRawMachineEventData =
  (data: RetrieveRawMachineEventDataRequestDto, signal?: AbortSignal) => async (dispatch: AppDispatch) => {
    return await dispatch(
      backendCall(createApiCall((api) => api.machines.retrieveRawMachineEventData, { params: data, signal }))
    );
  };

//reducer
export default createReducer<MachinesV2State>(initState, {
  [setIsMachinesSelectModalOpenAction.type]: (state: MachinesV2State, action: { payload: boolean }) => {
    return { ...state, isMachinesSelectModalOpen: action.payload };
  },
  [setModalMissingPIOpenAction.type]: (state: MachinesV2State, action: { payload: boolean }) => {
    return { ...state, modalMissingPIOpen: action.payload };
  },
  [setActionsModalOpenAction.type]: (state: MachinesV2State, action: { payload: boolean }) => {
    return { ...state, actionsModalOpen: action.payload };
  },
  [setChartDataAction.type]: (state: MachinesV2State, action: { payload: Record<string, unknown> }) => {
    return { ...state, chartData: action.payload };
  },
  [setSelectedTimeCounterAction.type]: (state: MachinesV2State) => {
    state.selectedTimeCounter += 1;
  },
  [setSelectedChartDateAction.type]: (state: MachinesV2State, action: { payload: string }) => {
    return { ...state, selectedChartDate: action.payload };
  },
  [setSelectedChartDateEndAction.type]: (state: MachinesV2State, action: { payload: string }) => {
    return { ...state, selectedChartDateEnd: action.payload };
  },
  [setInitialLoadingAction.type]: (state: MachinesV2State, action: { payload: boolean }) => {
    return { ...state, initialLoading: action.payload };
  },
  [setInitialEventDateAction.type]: (state: MachinesV2State, action: { payload: string | null }) => {
    return { ...state, initialEventDate: action.payload };
  },
  [setAllowLoadMoreDataAction.type]: (state: MachinesV2State, action: { payload: boolean }) => {
    return { ...state, allowLoadMoreData: action.payload };
  },
  [setLoadDataDisabledAction.type]: (state: MachinesV2State, action: { payload: boolean }) => {
    return { ...state, loadDataDisabled: action.payload };
  },
  [setDisplayScrollViewLoaderAction.type]: (state: MachinesV2State, action: { payload: boolean }) => {
    return { ...state, displayScrollViewLoader: action.payload };
  },
  [setSelectedGroupAction.type]: (state: MachinesV2State, action: { payload: MachineGroup | null }) => {
    return { ...state, selectedGroup: action.payload };
  },
  [setStatusesCountAction.type]: (state: MachinesV2State, action: { payload: StatusesCount }) => {
    return { ...state, statusesCount: action.payload };
  },
  [setIsRemovingPIAction.type]: (state: MachinesV2State, action: { payload: boolean }) => {
    return { ...state, isRemovingPI: action.payload };
  },
  [setFirstRenderMachinesProviderAction.type]: (state: MachinesV2State, action: { payload: boolean }) => {
    return { ...state, firstRenderMachinesProvider: action.payload };
  },
  [setShouldShowGlobalFullNCFileButtonAction.type]: (state: MachinesV2State, action: { payload: boolean }) => {
    return { ...state, shouldShowGlobalFullNCFileButton: action.payload };
  },
  [setSelectedMachineRunningTimeAction.type]: (state: MachinesV2State, action: { payload: number }) => {
    return { ...state, selectedMachineRunningTime: action.payload };
  },
});
